import { useState, useEffect, useCallback } from "react";
import { checkForToken } from "../utils/chat";
import defaultQuestions from "../defaultQuestions.json";
import defaultOnBoardingQuestions from "../defaultOnBoardingQuestions.json";
import defaultJobSeekerObBoardingQuestions from "../defaultJobSeekerObBoardingQuestions.json";
import { assessmentQuestionnaire } from "../utils/chat";
import evaluateCondition from "../utils/followUpQuestionHandler";

export const useQuestionLoader = (userType) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [loadedMessages, setLoadedMessages] = useState(null);
  const [loadedQuestions, setLoadedQuestions] = useState(null);
  const [initialQuestion, setInitialQuestion] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);

  const fetchOnBoardingQuestionsFromAPI = async (usertype) => {
    const searchParams = new URLSearchParams(window.location.search);
    const job = searchParams.get("job");
    const jobseeker = searchParams.get("j");
    await new Promise((resolve) => setTimeout(resolve, 500));

    const user = usertype === "c" ? "employer" : "jobseeker";
    let questions = [];
    let answered = [];

    if (usertype === "c") {
      questions = defaultOnBoardingQuestions.onboardingQuestions[user];
    } else if (usertype === "j") {
      questions = defaultJobSeekerObBoardingQuestions.onboardingQuestions[user];
    } else if (usertype === "a") {
      const questionnaire = await assessmentQuestionnaire(job, jobseeker);
      questions = questionnaire.questions || [];
      answered = questionnaire.answered || [];
      setAnsweredQuestions(questionnaire.answered || []);
    }
    return { questions, answered };
  };

  // Helper function to find a question by ID in the questions array or follow-up questions
  const findQuestionById = (questions, targetId) => {
    for (const question of questions) {
      if (question.id === targetId || question._id === targetId) {
        return question;
      }

      // Check follow-up questions
      if (
        question.follow_up_questions &&
        question.follow_up_questions.length > 0
      ) {
        for (const followUp of question.follow_up_questions) {
          if (
            followUp.question.id === targetId ||
            followUp.question._id === targetId
          ) {
            return followUp.question;
          }
        }
      }
    }
    return null;
  };

  // Function to build the complete question queue including follow-ups
  const buildQuestionQueue = (questions, answered) => {
    let questionQueue = [];
    let processedFollowUps = new Set();

    // Process each main question
    for (const question of questions) {
      // Check if this question was answered
      const answeredQuestion = answered.find(
        (ans) => ans.id === question.id || ans._id === question._id,
      );

      if (answeredQuestion) {
        // If answered, check for follow-up questions that should be added
        if (
          question.follow_up_questions &&
          question.follow_up_questions.length > 0
        ) {
          for (const followUp of question.follow_up_questions) {
            const followUpId = followUp.question.id || followUp.question._id;

            // Skip if already processed
            if (processedFollowUps.has(followUpId)) {
              continue;
            }

            // Check if this follow-up was already answered
            const followUpAnswered = answered.find(
              (ans) => ans.id === followUpId || ans._id === followUpId,
            );

            if (followUpAnswered) {
              // Follow-up was answered, mark as processed
              processedFollowUps.add(followUpId);
              continue;
            }

            // Check if condition is met for this follow-up
            let answerText =
              answeredQuestion.answer_text || answeredQuestion.text || "";

            // Handle different answer formats
            if (typeof answerText === "string" && answerText.includes(", ")) {
              // Multiple choice answers might be comma-separated
              answerText = answerText.split(", ");
            }

            if (
              evaluateCondition(
                followUp.condition,
                followUp.condition_value,
                answerText,
              )
            ) {
              // Condition met, add to queue
              questionQueue.push(followUp.question);
              processedFollowUps.add(followUpId);
            }
          }
        }
      } else {
        // Question not answered, add to queue
        questionQueue.push(question);
      }
    }

    return questionQueue;
  };

  // Function to find the next unanswered question
  const findNextQuestion = (questions, answered) => {
    const questionQueue = buildQuestionQueue(questions, answered);

    // Return the first question in the queue (next to be answered)
    return questionQueue.length > 0 ? questionQueue[0] : null;
  };

  // Function to rebuild conversation messages from answered questions
  const rebuildConversationMessages = (questions, answered) => {
    let messages = [];

    // Add welcome message
    const defaultMessage = {
      text:
        userType === "j"
          ? "Welcome! Let's begin with the Registration process."
          : "Welcome! Let's begin the interview.",
      sender: "bot",
    };
    messages.push(defaultMessage);

    // Process answered questions in order
    const sortedAnswered = [...answered].sort((a, b) => {
      // Sort by timestamp if available, otherwise by order
      const aTime = new Date(a.createdAt || a.timestamp || 0);
      const bTime = new Date(b.createdAt || b.timestamp || 0);
      return aTime - bTime;
    });

    for (const answeredQ of sortedAnswered) {
      // Find the original question
      const originalQuestion = findQuestionById(
        questions,
        answeredQ.id || answeredQ._id,
      );

      if (originalQuestion) {
        // Add bot question message
        messages.push({
          text: originalQuestion.question_text,
          sender: "bot",
          isQuestion: true,
          required: originalQuestion.required,
        });

        // Add user answer message
        const userMessage = {
          text: answeredQ.answer_text || answeredQ.text || "",
          sender: "user",
          files: answeredQ.files || [],
          user: answeredQ.user || {},
        };
        messages.push(userMessage);
      }
    }

    return messages;
  };

  const loadQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const { questions, answered } =
        await fetchOnBoardingQuestionsFromAPI(userType);

      if (questions.length > 0) {
        // Rebuild conversation from answered questions
        const conversationMessages = rebuildConversationMessages(
          questions,
          answered,
        );

        // Find next unanswered question
        const nextQuestion = findNextQuestion(questions, answered);

        if (nextQuestion) {
          // Add the next question to messages
          const nextQuestionMessage = {
            text: nextQuestion.question_text,
            sender: "bot",
            isQuestion: true,
            required: nextQuestion.required,
          };
          conversationMessages.push(nextQuestionMessage);

          // Build the remaining question queue
          const questionQueue = buildQuestionQueue(questions, answered);

          setLoadedMessages(conversationMessages);
          setLoadedQuestions(questionQueue);
          setInitialQuestion(nextQuestion);
        } else {
          // All questions answered
          const completionMessage = {
            text:
              userType === "a"
                ? "Thank you for completing the questionnaire. Your responses will be reviewed shortly."
                : "Thank you for completing the registration. We will send you an email shortly with further details.",
            sender: "bot",
          };
          conversationMessages.push(completionMessage);

          setLoadedMessages(conversationMessages);
          setLoadedQuestions([]);
          setInitialQuestion(null);
        }
      } else {
        const defaultMessage = {
          text:
            userType === "j"
              ? "Welcome! Let's begin with the Registration process."
              : "Welcome! Let's begin the interview.",
          sender: "bot",
        };
        setLoadedMessages([defaultMessage]);
        setLoadedQuestions([]);
        setInitialQuestion(null);
      }
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      setLoadedMessages([
        {
          text: "Sorry, we couldn't load the questions. Please try again later.",
          sender: "bot",
        },
      ]);
      setLoadedQuestions([]);
      setInitialQuestion(null);
    } finally {
      setIsLoading(false);
    }
  }, [userType]);

  useEffect(() => {
    const validateAndLoad = async () => {
      try {
        /* to-do remove after testing */
        const valid = await checkForToken("assessment");
        if (valid) {
          await loadQuestions();
          setIsTokenValid(true);
        } else {
          setLoadedMessages([
            { text: "Link is Expired or Used", sender: "bot" },
          ]);
          setIsTokenValid(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error validating token:", error);
        setIsTokenValid(false);
        setIsLoading(false);
      }
    };

    validateAndLoad();
  }, [loadQuestions]);

  return {
    isLoading,
    isTokenValid,
    loadedMessages,
    loadedQuestions,
    initialQuestion,
    answeredQuestions,
  };
};
